import type PriceStackLuxPlusProminentPrice from 'LuxKit/PricePoints/LuxPlus/PriceStackLuxPlusProminentPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import PriceStackPriceWithLoyalty from 'LuxKit/PricePoints/PriceStackPriceWithLoyalty'
import PriceStackPriceWithLoyaltyLeadWithRegularPrice from 'LuxKit/PricePoints/PriceStackPriceWithLoyaltyLeadWithRegularPrice'
import PriceRowLuxPlusPriceBox from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceBox'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceStackLuxPlusCompactPrice from 'LuxKit/PricePoints/LuxPlus/PriceStackLuxPlusCompactPrice'
import PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice from 'LuxKit/PricePoints/PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice'
import { isGoogleHotelAdsBot } from 'selectors/accountSelectors'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'

type InheritedPriceRowPriceProps = Pick<ComponentProps<typeof PriceRowPrice>, 'signDisplay' | 'size'>
type InheritedPriceStackPriceWithLoyaltyProps = Pick<ComponentProps<typeof PriceStackPriceWithLoyalty>, 'horizontalAlign' | 'desktopHorizontalAlign'>

interface CommonProps extends InheritedPriceRowPriceProps, InheritedPriceStackPriceWithLoyaltyProps {
  testid?: string
  /**
   * @default vertical
   */
  orientation?: ComponentProps<typeof PriceStackLuxPlusProminentPrice>['orientation']
  offerConfig?: App.OfferLuxPlusConfig
  isTour?: boolean
  mode?: App.LuxPlusPriceStackMode
}

interface PropsWithSeparatePriceFields extends CommonProps {
  price: number
  saleUnit: string
  memberPrice?: number
}

interface PropsWithPricingField extends CommonProps {
  pricing: App.Pricing
  memberPricing?: App.Pricing
}

type Props = PropsWithSeparatePriceFields | PropsWithPricingField

function LuxPlusPriceStack(props: Props) {
  const {
    size,
    testid,
    signDisplay,
    horizontalAlign = 'start',
    desktopHorizontalAlign,
    orientation = 'vertical',
    offerConfig,
    isTour = false,
    mode,
  } = props

  let memberPrice: number = 0
  let price: number
  let saleUnit: string

  if ('pricing' in props) {
    price = props.pricing.price
    saleUnit = props.pricing.saleUnit
    memberPrice = props.memberPricing?.price || 0
  } else {
    price = props.price
    saleUnit = props.saleUnit
    memberPrice = props.memberPrice || 0
  }

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const luxPlusTourEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const isGoogleHotelAds = useAppSelector(isGoogleHotelAdsBot)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)

  const displayedMode = useMemo(() => {
    if (!luxPlusEnabled) {
      return 'regular-only'
    } else if (isGhaPricing && memberPrice > 0 && memberPrice < price) {
      return 'regular-prominent'
    } else if (mode) {
      return mode
    } else if (!!isTour && !luxPlusTourEnabled) {
      // This case will be ignored for all non-tours
      // For tours, this case will return below when toggle is off
      // if toggle is on, this case is ignored and regular logic (in the following cases) is used to display
      // the component appropriately
      return 'regular-only'
    } else if (!canViewLuxPlusBenefits && memberPrice > 0 && memberPrice < price) {
      return 'regular-prominent'
    } else if (offerConfig?.access === 'memberOnly') {
      return 'member-only'
    } else if (canViewLuxPlusBenefits && memberPrice > 0 && memberPrice < price) {
      return 'member-prominent'
    } else {
      return 'regular-only'
    }
  }, [canViewLuxPlusBenefits, isTour, offerConfig?.access, luxPlusEnabled, luxPlusTourEnabled, memberPrice, price, mode, isGhaPricing])

  switch (displayedMode) {
    case 'member-only':
      return <PriceRowLuxPlusPriceBox
        data-testid={testid}
        memberPrice={memberPrice || price}
        saleUnit={saleUnit}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-only':
      return <PriceRowPrice
        data-testid={testid}
        price={price}
        saleUnit={saleUnit}
        size={size}
        signDisplay={signDisplay}
      />
    case 'member-prominent': {
      return <PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        signDisplay={signDisplay}
        horizontalAlign={horizontalAlign}
        orientation={orientation}
        desktopHorizontalAlign={desktopHorizontalAlign}
      />
    }
    case 'member-compact':
      return <PriceStackLuxPlusCompactPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-prominent':
      if (isGhaPricing) {
        return <PriceStackPriceWithLoyaltyLeadWithRegularPrice
          data-testid={testid}
          memberPrice={memberPrice}
          saleUnit={saleUnit}
          nonMemberPrice={price}
          size={size}
          signDisplay={signDisplay}
          horizontalAlign={horizontalAlign}
          desktopHorizontalAlign={desktopHorizontalAlign}
        />
      }
      if (isGoogleHotelAds) {
        return <PriceStackPriceWithLoyalty
          data-testid={testid}
          memberPrice={memberPrice}
          saleUnit={saleUnit}
          nonMemberPrice={price}
          size={size}
          signDisplay={signDisplay}
          horizontalAlign={horizontalAlign}
          desktopHorizontalAlign={desktopHorizontalAlign}
        />
      }
      return <PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        signDisplay={signDisplay}
        horizontalAlign={horizontalAlign}
        orientation={orientation}
        desktopHorizontalAlign={desktopHorizontalAlign}
      />

    case 'member-checkout-total':
      return <PriceRowLuxPlusPriceBox
        data-testid={testid}
        memberPrice={memberPrice || price}
        saleUnit={saleUnit}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-with-luxPlus':
      return <PriceRowLuxPlusPrice
        size="L"
        memberPrice={memberPrice}
        logoPlacement="trailing"
        saleUnit=""
      />
  }
}

export default LuxPlusPriceStack
